import { useContext } from 'react';
import AvailabilityAction from '~/constants/availabilityAction';
import AvailabilityError from '~/constants/availabilityError';
import PrimaryCtaType from '~/constants/PrimaryCtaType';
import { UserVehicleContext } from '~/context/vdp/UserVehicleContext';
import { CurrentWipStatus } from '~/contracts/vdp/Availability';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsVehicleReservedByOther from '~/hooks/vdp/selectors/useIsVehicleReservedByOther';
import useIsVehicleReservedByUser from './useIsVehicleReservedByUser';
import useIsWipVehicleReservedByUser from './useIsWipVehicleReservedByUser';

export default function usePrimaryCtaButtonState() {
  const {
    isSubscribedToNotifications,
    vehicleAvailability: {
      data: {
        currentWipStatus: { status: wipStatus = CurrentWipStatus.Unknown } = {},
        action,
        error
      },
      isLoaded: isAvailabilityLoaded
    }
  } = useContext(UserVehicleContext);
  const { isSold, otherUnavailable } = useAlertStatus();
  const isReservedByOther = useIsVehicleReservedByOther();
  const isWipReservedByUser = useIsWipVehicleReservedByUser();
  const isReservedByUser = useIsVehicleReservedByUser();
  const isReservedByOtherAndSubscribed =
    isReservedByOther && isSubscribedToNotifications;
  const isReservedByUserAndKicked =
    (isWipReservedByUser || isReservedByUser) &&
    wipStatus === CurrentWipStatus.Kicked;

  const isLoading = !isAvailabilityLoaded;

  // button state logic
  const cancelPreOrder =
    isWipReservedByUser && wipStatus !== CurrentWipStatus.Kicked;
  const continuePurchase =
    action === AvailabilityAction.ContinuePurchase ||
    error === AvailabilityError.OnSitePrivate;
  const disabledGetStarted =
    action === AvailabilityAction.GetStartedDisabled &&
    error !== AvailabilityError.IsSold &&
    error !== AvailabilityError.Unavailable &&
    !otherUnavailable;
  const disabledPreOrder = action === AvailabilityAction.PreOrderNowDisabled;
  const getAlerts = isReservedByOther && !isSubscribedToNotifications;
  const getStarted = action === AvailabilityAction.GetStarted;
  const preOrderNow = action === AvailabilityAction.PreOrderNow;
  const viewSimilarVehicles =
    isReservedByOtherAndSubscribed ||
    isReservedByUserAndKicked ||
    action === AvailabilityAction.NotifyMe ||
    isSold ||
    error === AvailabilityError.Unavailable ||
    otherUnavailable;

  const type = (() => {
    switch (true) {
      case continuePurchase:
        return PrimaryCtaType.ContinuePurchase;
      case getStarted:
        return PrimaryCtaType.GetStarted;
      case disabledGetStarted:
        return PrimaryCtaType.DisabledGetStarted;
      case cancelPreOrder:
        return PrimaryCtaType.CancelPreOrder;
      case viewSimilarVehicles:
        return PrimaryCtaType.ViewSimilarVehicles;
      case getAlerts:
        return PrimaryCtaType.GetAlerts;
      case preOrderNow:
        return PrimaryCtaType.Reservable;
      case disabledPreOrder:
        return PrimaryCtaType.DisabledPreOrder;
      default:
        return PrimaryCtaType.Default;
    }
  })();

  // Disable Get started or Pre order now buttons when availability loads
  const isDisabled =
    !isAvailabilityLoaded ||
    type === PrimaryCtaType.DisabledGetStarted ||
    type === PrimaryCtaType.DisabledPreOrder;

  return {
    continuePurchase,
    getStarted,
    isDisabled,
    isLoading,
    isReservedByOtherAndSubscribed,
    isReservedByUserAndKicked,
    type
  };
}
